import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, X, Trophy, Shield, Gift, Info, Mail, Megaphone, FileText, Clock, Bell } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import notificationService from '../../services/notificationService';
import { parseServerDate } from '../../utils/dateUtils';
import { getFallbackNoticeText } from '../../utils/messageLang';

// Détecte si une notification doit afficher un fallback langue (titleFr/titleEn + bodyFr/bodyEn)
const resolveNotifLang = (item, requestedLang) => {
    const hasFr = !!((item.titleFr && item.titleFr.trim()) || (item.bodyFr && item.bodyFr.trim()));
    const hasEn = !!((item.titleEn && item.titleEn.trim()) || (item.bodyEn && item.bodyEn.trim()));
    const hasReq = requestedLang === 'fr' ? hasFr : hasEn;
    const otherLang = requestedLang === 'fr' ? 'en' : 'fr';
    const hasOther = otherLang === 'fr' ? hasFr : hasEn;
    if (hasReq) return { actualLang: requestedLang, isFallback: false };
    if (hasOther) return { actualLang: otherLang, isFallback: true };
    return { actualLang: requestedLang, isFallback: false }; // both empty: silent
};

// ── Icon config per type ────────────────────────────────────────
const ICON_CONFIG = {
    trophy:    { Icon: Trophy,    bg: 'bg-amber-100 dark:bg-amber-500/20',   text: 'text-amber-500' },
    badge:     { Icon: Shield,    bg: 'bg-blue-100 dark:bg-blue-500/20',     text: 'text-blue-500' },
    gift:      { Icon: Gift,      bg: 'bg-green-100 dark:bg-green-500/20',   text: 'text-green-500' },
    mail:      { Icon: Mail,      bg: 'bg-indigo-100 dark:bg-indigo-500/20', text: 'text-indigo-500' },
    megaphone: { Icon: Megaphone, bg: 'bg-purple-100 dark:bg-purple-500/20', text: 'text-purple-500' },
    document:  { Icon: FileText,  bg: 'bg-emerald-100 dark:bg-emerald-500/20', text: 'text-emerald-500' },
    clock:     { Icon: Clock,     bg: 'bg-orange-100 dark:bg-orange-500/20', text: 'text-orange-500' },
    info:      { Icon: Info,      bg: 'bg-gray-100 dark:bg-gray-500/20',     text: 'text-gray-500' },
};

const getIconConfig = (icon) => ICON_CONFIG[icon] || ICON_CONFIG.info;

// ── Relative time ───────────────────────────────────────────────
const getRelativeTime = (dateStr, lang) => {
    const date = parseServerDate(dateStr);
    if (!date) return '';
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    const diffH = Math.floor(diffMs / 3600000);
    const diffD = Math.floor(diffMs / 86400000);

    if (diffMin < 1) return lang === 'fr' ? "A l'instant" : 'Just now';
    if (diffMin < 60) return lang === 'fr' ? `Il y a ${diffMin} min` : `${diffMin}m ago`;
    if (diffH < 24) return lang === 'fr' ? `Il y a ${diffH}h` : `${diffH}h ago`;
    if (diffD < 7) return lang === 'fr' ? `Il y a ${diffD}j` : `${diffD}d ago`;
    return date.toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA', { day: 'numeric', month: 'short', year: 'numeric' });
};

// ── Badge key extractor ──────────────────────────────────────────
const getBadgeKey = (item) => {
    if (item.type !== 'badge_unlocked' || !item.meta?.relatedUrl) return null;
    const parts = item.meta.relatedUrl.split(':');
    return parts[2] || null;
};

// ── Notification card ───────────────────────────────────────────
const NotificationCard = ({ item, language, onClick }) => {
    const { Icon, bg, text } = getIconConfig(item.icon);
    const { actualLang } = resolveNotifLang(item, language);
    const title = actualLang === 'fr' ? item.titleFr : item.titleEn;
    const body = actualLang === 'fr' ? item.bodyFr : item.bodyEn;
    const isUnread = !item.isRead;
    const badgeKey = getBadgeKey(item);

    return (
        <button
            onClick={onClick}
            className={`w-full text-left flex items-center gap-4 p-4 rounded-xl transition-all duration-200 group ${
                isUnread
                    ? 'bg-white dark:bg-gray-800 shadow-sm hover:shadow-md border border-gray-100 dark:border-gray-700'
                    : 'bg-gray-50/50 dark:bg-gray-800/40 hover:bg-gray-100/50 dark:hover:bg-gray-800/60 opacity-70'
            }`}
        >
            {/* Unread dot */}
            <div className="flex-shrink-0 w-2 flex justify-center">
                {isUnread && <span className="w-2 h-2 bg-red-500 rounded-full" />}
            </div>

            {/* Icon — badge image or generic icon */}
            {badgeKey ? (
                <div className="flex-shrink-0">
                    <img
                        src={`/img/application/rewards/${badgeKey}.png`}
                        alt=""
                        className="w-11 h-11 object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                </div>
            ) : (
                <div className={`flex-shrink-0 p-2.5 rounded-xl ${bg} ${text}`}>
                    <Icon size={20} />
                </div>
            )}

            {/* Content */}
            <div className="flex-1 min-w-0">
                <h4 className={`text-sm font-semibold truncate ${isUnread ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'}`}>
                    {title}
                </h4>
                {body && (
                    <p className={`text-xs mt-0.5 truncate ${isUnread ? 'text-gray-500 dark:text-gray-400' : 'text-gray-400 dark:text-gray-500'}`}>
                        {body.substring(0, 100)}
                    </p>
                )}
            </div>

            {/* Date + chevron */}
            <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap hidden sm:block">
                    {getRelativeTime(item.createdAt, language)}
                </span>
                <ChevronRight size={16} className="text-gray-300 dark:text-gray-600 group-hover:text-gray-400 dark:group-hover:text-gray-500 transition" />
            </div>
        </button>
    );
};

// ── Detail modal ────────────────────────────────────────────────
const NotificationDetailModal = ({ item, language, onClose }) => {
    useEffect(() => {
        const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [onClose]);

    if (!item) return null;

    const { Icon, bg, text } = getIconConfig(item.icon);
    const { actualLang, isFallback } = resolveNotifLang(item, language);
    const rawTitle = actualLang === 'fr' ? item.titleFr : item.titleEn;
    const body = actualLang === 'fr' ? item.bodyFr : item.bodyEn;
    const date = (parseServerDate(item.createdAt) || new Date()).toLocaleDateString(
        language === 'fr' ? 'fr-CA' : 'en-CA',
        { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }
    );

    // For badge notifications, extract just the badge name (e.g. "Copilote" from "Badge débloqué : Copilote")
    const badgeName = item.type === 'badge_unlocked' && rawTitle?.includes(' : ')
        ? rawTitle.split(' : ').slice(1).join(' : ')
        : null;
    const title = badgeName || rawTitle;

    // Type-specific label
    const typeLabels = {
        message:                { fr: 'Message', en: 'Message' },
        broadcast:              { fr: 'Communication', en: 'Communication' },
        badge_unlocked:         { fr: 'Badge débloqué', en: 'Badge unlocked' },
        achievement_code:       { fr: 'Code promo', en: 'Promo code' },
        recommendation_letter:  { fr: 'Lettre de recommandation', en: 'Recommendation letter' },
        expiry_alert:           { fr: 'Alerte expiration', en: 'Expiry alert' },
    };
    const typeLabel = typeLabels[item.type]?.[language] || (language === 'fr' ? 'Notification' : 'Notification');

    return (
        <div
            className="fixed inset-0 z-[110] flex items-center justify-center p-4 md:p-6 bg-black/60 backdrop-blur-sm"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="flex items-center gap-3 p-5 border-b border-gray-100 dark:border-gray-800">
                    <div className={`p-2.5 rounded-xl ${bg} ${text}`}>
                        <Icon size={22} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <span className={`text-xs font-bold uppercase tracking-wider ${text}`}>{typeLabel}</span>
                        <h2 className="text-base font-bold text-gray-900 dark:text-white leading-tight mt-0.5">{title}</h2>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition">
                        <X size={18} className="text-gray-400" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    {/* Banner if notification is shown in fallback language */}
                    {isFallback && (
                        <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 text-xs text-amber-700 dark:text-amber-300 flex items-start gap-2">
                            <Info size={14} className="flex-shrink-0 mt-0.5" />
                            <span>{getFallbackNoticeText(language)}</span>
                        </div>
                    )}
                    {/* Broadcast level badge */}
                    {item.meta?.broadcastLevel && (
                        <span className="inline-block px-2.5 py-1 rounded-full text-xs font-bold bg-purple-100 dark:bg-purple-500/10 text-purple-700 dark:text-purple-400">
                            {item.meta.broadcastLevel.toUpperCase()}
                        </span>
                    )}

                    {/* Badge unlocked — centered image + bravo message */}
                    {item.type === 'badge_unlocked' && (() => {
                        const badgeKey = getBadgeKey(item);
                        return (
                            <div className="text-center space-y-3 py-2">
                                {badgeKey && (
                                    <div className="flex justify-center">
                                        <img
                                            src={`/img/application/rewards/${badgeKey}.png`}
                                            alt={title}
                                            className="w-28 h-28 object-contain drop-shadow-lg"
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                    </div>
                                )}
                                <p className="text-base font-bold text-gray-900 dark:text-white">
                                    {language === 'fr' ? 'Bravo, tu as obtenu :' : 'Congratulations, you earned:'}
                                </p>
                                <p className="text-lg font-black text-amber-600 dark:text-amber-400">
                                    {title}
                                </p>
                                {body && (
                                    <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mt-2">
                                        {body}
                                    </p>
                                )}
                            </div>
                        );
                    })()}

                    {/* Body text (non-badge) */}
                    {item.type !== 'badge_unlocked' && body && (
                        <div className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                            {body}
                        </div>
                    )}

                    {/* Achievement code highlight */}
                    {item.type === 'achievement_code' && body && (() => {
                        const codeMatch = body.match(/:\s*(ACH-[A-Z0-9-]+)/i);
                        if (!codeMatch) return null;
                        return (
                            <div className="p-4 bg-amber-50 dark:bg-amber-500/10 rounded-xl border border-amber-200 dark:border-amber-500/20 text-center">
                                <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 mb-1">
                                    {language === 'fr' ? 'Votre code promo' : 'Your promo code'}
                                </p>
                                <p className="text-xl font-black tracking-wider text-amber-800 dark:text-amber-300 select-all">
                                    {codeMatch[1]}
                                </p>
                            </div>
                        );
                    })()}

                    {/* Related URL action button */}
                    {item.meta?.relatedUrl && item.meta.relatedUrl.startsWith('/') && (
                        <RelatedUrlButton url={item.meta.relatedUrl} type={item.type} language={language} onClose={onClose} />
                    )}

                    {/* Contact footer for messages */}
                    {(item.source === 'message' || item.type === 'message' || item.type === 'broadcast') && (
                        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                            <p className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed">
                                {language === 'fr'
                                    ? 'Besoin de plus d\'informations ? Contactez-nous à '
                                    : 'Need more information? Contact us at '}
                                <a
                                    href="mailto:service@nizus.com"
                                    className="font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    service@nizus.com
                                </a>
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
                    <span className="text-xs text-gray-400 dark:text-gray-500">{date}</span>
                    <button
                        onClick={onClose}
                        className="px-4 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition"
                    >
                        {language === 'fr' ? 'Fermer' : 'Close'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const RelatedUrlButton = ({ url, type, language, onClose }) => {
    const navigate = useNavigate();

    const labels = {
        recommendation_letter: { fr: 'Voir mes lettres', en: 'View my letters' },
        expiry_alert:          { fr: 'Renouveler', en: 'Renew' },
    };
    const label = labels[type]?.[language] || (language === 'fr' ? 'Voir' : 'View');

    return (
        <button
            onClick={() => { onClose(); navigate(url); }}
            className="w-full py-2.5 text-sm font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10 hover:bg-blue-100 dark:hover:bg-blue-500/20 rounded-xl transition text-center"
        >
            {label}
        </button>
    );
};

// ── Main component ──────────────────────────────────────────────
const Notifications = () => {
    const { language } = useLanguage();
    const { refreshNotificationCount } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedItem, setSelectedItem] = useState(null);

    const fetchItems = useCallback(async () => {
        try {
            const data = await notificationService.getUnifiedList();
            setItems(data.items || []);
        } catch (err) {
            console.error('Failed to load notifications', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchItems();
    }, [fetchItems]);

    const handleItemClick = async (item) => {
        // Mark as read if unread
        if (!item.isRead) {
            try {
                await notificationService.markUnifiedRead(item.id);
                setItems(prev => prev.map(i => i.id === item.id ? { ...i, isRead: 1 } : i));
                refreshNotificationCount();
            } catch (err) {
                console.error('Failed to mark as read', err);
            }
        }
        setSelectedItem(item);
    };

    const handleMarkAllRead = async () => {
        try {
            await notificationService.markAllUnifiedRead();
            setItems(prev => prev.map(i => ({ ...i, isRead: 1 })));
            refreshNotificationCount();
        } catch (err) {
            console.error('Failed to mark all as read', err);
        }
    };

    const unread = items.filter(i => !i.isRead);
    const read = items.filter(i => i.isRead);

    if (loading) {
        return (
            <div className="p-8 flex justify-center">
                <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="pb-12">
            {/* Hero Banner */}
            <div className="relative overflow-hidden rounded-[2rem] p-10 h-64 bg-gradient-to-br from-[#bf360c] via-[#d84315] to-[#ff6e40] border border-white/5 !mb-8 shadow-xl text-white">
                <div className="absolute inset-0 opacity-20 pointer-events-none"
                    style={{
                        backgroundImage: 'linear-gradient(90deg, transparent 50%, rgba(0,0,0,0.5) 50%)',
                        backgroundSize: '4px 100%'
                    }}
                />
                <div className="relative z-10 h-full flex flex-col justify-between">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-white/20 rounded-2xl">
                            <Bell size={24} />
                        </div>
                        <h1 className="text-4xl font-black text-white tracking-tight">Notifications</h1>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 sm:gap-4">
                        <p className="text-white/60 text-sm sm:text-lg max-w-sm leading-snug">
                            {language === 'fr'
                                ? 'Alertes, messages, codes promo et récompenses.'
                                : 'Alerts, messages, promo codes and rewards.'}
                        </p>
                        {unread.length > 0 && (
                            <button
                                onClick={handleMarkAllRead}
                                className="flex-shrink-0 self-start sm:self-auto px-4 py-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl text-xs sm:text-sm font-medium transition-colors"
                            >
                                {language === 'fr' ? 'Tout marquer comme lu' : 'Mark all as read'}
                            </button>
                        )}
                    </div>
                </div>
                <div className="absolute right-[-40px] bottom-[-40px] opacity-20 pointer-events-none">
                    <Bell size={288} strokeWidth={0.5} />
                </div>
            </div>

            {/* Empty state */}
            {items.length === 0 && (
                <div className="text-center py-16">
                    <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
                        <Bell size={32} className="text-gray-400" />
                    </div>
                    <p className="text-gray-500 dark:text-gray-400 font-medium">
                        {language === 'fr' ? 'Aucune notification pour le moment' : 'No notifications yet'}
                    </p>
                    <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">
                        {language === 'fr'
                            ? 'Vos alertes, messages et codes promo apparaîtront ici.'
                            : 'Your alerts, messages and promo codes will appear here.'}
                    </p>
                </div>
            )}

            {/* Unread section */}
            {unread.length > 0 && (
                <div className="mb-8">
                    <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 px-1">
                        {language === 'fr' ? 'Non lus' : 'Unread'}
                        <span className="ml-2 px-2 py-0.5 bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400 rounded-full text-xs normal-case">
                            {unread.length}
                        </span>
                    </h2>
                    <div className="space-y-2">
                        {unread.map(item => (
                            <NotificationCard key={item.id} item={item} language={language} onClick={() => handleItemClick(item)} />
                        ))}
                    </div>
                </div>
            )}

            {/* Read section */}
            {read.length > 0 && (
                <div>
                    <h2 className="text-sm font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3 px-1">
                        {language === 'fr' ? 'Déjà lus' : 'Already read'}
                    </h2>
                    <div className="space-y-2">
                        {read.map(item => (
                            <NotificationCard key={item.id} item={item} language={language} onClick={() => handleItemClick(item)} />
                        ))}
                    </div>
                </div>
            )}

            {/* Detail modal */}
            {selectedItem && (
                <NotificationDetailModal
                    item={selectedItem}
                    language={language}
                    onClose={() => setSelectedItem(null)}
                />
            )}
        </div>
    );
};

export default Notifications;
